/**
 * T89 — Addy AI Assistant connection management (ADR-0016, the single
 * HIS-side AI surface). HIS issues the service-account API key; the scope
 * set is the fixed 8 contracts — this client never adds new ones.
 */
import { apiClient } from "./client";

export interface AiConnectionStatus {
  status: "CONNECTED" | "DISCONNECTED";
  instanceUrl: string | null;
  apiKeyPrefix: string | null;
  enabledScopes: string[];
  connectedAt: string | null;
  keyRotatedAt: string | null;
  /** Non-null ONLY in the connect / regenerate response — shown once. */
  apiKeyPlaintext: string | null;
}

export interface ScopeCatalog {
  phiFree: string[];
  phiPresent: string[];
}

/** Vietnamese labels for the 8 fixed scopes (ADR-0016). */
export const SCOPE_LABELS: Record<string, string> = {
  "service:read": "Đọc danh mục dịch vụ",
  "doctor:read": "Đọc danh sách bác sĩ",
  "branch:read": "Đọc chi nhánh",
  "slot:read": "Đọc khung giờ trống",
  "intake:create": "Tạo hồ sơ tiếp nhận (nháp)",
  "appointment:create": "Đặt lịch hẹn",
  "handoff:create": "Tạo yêu cầu chuyển tiếp",
  "patient:search": "Tìm bệnh nhân (đã che thông tin)",
};

export function scopeLabel(s: string): string {
  return SCOPE_LABELS[s] ?? s;
}

export const integrationsApi = {
  get(): Promise<AiConnectionStatus> {
    return apiClient
      .get<AiConnectionStatus>("/v1/integrations/ai-assistant")
      .then((r) => r.data);
  },
  scopeCatalog(): Promise<ScopeCatalog> {
    return apiClient
      .get<ScopeCatalog>("/v1/integrations/ai-assistant/scopes")
      .then((r) => r.data);
  },
  connect(instanceUrl: string): Promise<AiConnectionStatus> {
    return apiClient
      .post<AiConnectionStatus>("/v1/integrations/ai-assistant/connect", {
        instanceUrl,
      })
      .then((r) => r.data);
  },
  regenerateKey(): Promise<AiConnectionStatus> {
    return apiClient
      .post<AiConnectionStatus>(
        "/v1/integrations/ai-assistant/regenerate-key"
      )
      .then((r) => r.data);
  },
  updateScopes(scopes: string[]): Promise<AiConnectionStatus> {
    return apiClient
      .put<AiConnectionStatus>("/v1/integrations/ai-assistant/scopes", {
        scopes,
      })
      .then((r) => r.data);
  },
  disconnect(): Promise<AiConnectionStatus> {
    return apiClient
      .post<AiConnectionStatus>("/v1/integrations/ai-assistant/disconnect")
      .then((r) => r.data);
  },
};

export const integrationsQueryKeys = {
  status: () => ["integrations", "ai-assistant"] as const,
  scopeCatalog: () => ["integrations", "ai-scopes"] as const,
};

/** Where the OpenAPI docs live (same origin as the API in prod). */
export function apiDocsUrls(): { swaggerUi: string; openapiJson: string } {
  const base =
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";
  // Absolute cross-origin API base → point docs at that backend origin.
  // Otherwise use same-origin relative paths: in dev the Vite proxy forwards
  // /swagger-ui + /v3/api-docs to :8080; in prod the reverse proxy does.
  let prefix = "";
  if (base.startsWith("http")) {
    try {
      prefix = new URL(base).origin;
    } catch {
      /* same-origin */
    }
  }
  return {
    swaggerUi: `${prefix}/swagger-ui/index.html`,
    openapiJson: `${prefix}/v3/api-docs`,
  };
}
